import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';
import { HubFeature } from '@ats/api/cm';

const pathStart = 'Catalogs|Badges|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ShareCertMetricsBadging,
	portal: 'SP',
};
const columnDescriptionStart = 'Column heading for: ';

type LocNameStart = 'SPCatalogsBadging';
export type SPCatalogsBadgingLocNames =
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}TitleSection`
	| `${LocNameStart}ButtonCriteria`
	| `${LocNameStart}ButtonCreateBadge`
	| `${LocNameStart}ContentNone`
	| `${LocNameStart}ContentBoth`
	| `${LocNameStart}ContentText`
	| `${LocNameStart}ContentHtml`
	| `${LocNameStart}LabelFilterByType`
	| `${LocNameStart}LabelType`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelCriteriaUrl`
	| `${LocNameStart}LabelDescription`
	| `${LocNameStart}LabelEnabled`
	| `${LocNameStart}LabelImage`;

export const spCatalogsBadgingLocs: LocDefs<SPCatalogsBadgingLocNames> = {
	SPCatalogsBadgingTitlePage: {
		defaultValue: 'CertMetrics Badge Catalog',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Title.PageTitle',
		...sharedProperties,
	},
	SPCatalogsBadgingTitleSection: {
		defaultValue: 'CertMetrics Badge Catalog',
		description: 'Section Title',
		displayPath: `${pathStart}Section Title`,
		displayType: 'Title',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Title.Title',
		...sharedProperties,
	},
	SPCatalogsBadgingButtonCriteria: {
		defaultValue: 'Criteria',
		description: 'Criteria button',
		displayPath: `${pathStart}Criteria`,
		displayType: 'Button',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Button.Criteria',
		...sharedProperties,
	},
	SPCatalogsBadgingButtonCreateBadge: {
		defaultValue: 'Create Badge',
		description: 'Create Badge button',
		displayPath: `${pathStart}Create Badge`,
		displayType: 'Button',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Button.CreateBadge',
		...sharedProperties,
	},
	SPCatalogsBadgingContentNone: {
		defaultValue: 'None',
		description: 'Text shown when there is no description.',
		displayPath: `${pathStart}None`,
		displayType: 'Content',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Content.None',
		...sharedProperties,
	},
	SPCatalogsBadgingContentBoth: {
		defaultValue: 'HTML + Text',
		description: 'Text shown when there is a text and HTML description.',
		displayPath: `${pathStart}HTML + Text`,
		displayType: 'Content',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Content.All',
		...sharedProperties,
	},
	SPCatalogsBadgingContentText: {
		defaultValue: 'Text',
		description: 'Text shown when there is a text description.',
		displayPath: `${pathStart}Text`,
		displayType: 'Content',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Content.Text',
		...sharedProperties,
	},
	SPCatalogsBadgingContentHtml: {
		defaultValue: 'HTML',
		description: 'Text shown when there is an HTML description.',
		displayPath: `${pathStart}HTML`,
		displayType: 'Content',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.Content.HTML',
		...sharedProperties,
	},
	SPCatalogsBadgingLabelFilterByType: {
		defaultValue: 'Filter by Type',
		description: 'Label for: Filter by Type.',
		displayPath: `${pathStart}Filter by Type`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.FieldLabel.FilterType',
		...sharedProperties,
	},
	SPCatalogsBadgingLabelType: {
		defaultValue: 'Type',
		description: `${columnDescriptionStart}Type.`,
		displayPath: `${pathStart}Type`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.ColumnHeading.Type',
		...sharedProperties,
	},
	SPCatalogsBadgingLabelName: {
		defaultValue: 'Name',
		description: `${columnDescriptionStart}Name.`,
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.ColumnHeading.Name',
		...sharedProperties,
	},
	SPCatalogsBadgingLabelCriteriaUrl: {
		defaultValue: 'Criteria URL',
		description: `${columnDescriptionStart}Criteria URL.`,
		displayPath: `${pathStart}Criteria URL`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.ColumnHeading.CriteriaUrl',
		...sharedProperties,
	},
	SPCatalogsBadgingLabelDescription: {
		defaultValue: 'Description',
		description: `${columnDescriptionStart}Description.`,
		displayPath: `${pathStart}Description`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.ColumnHeading.Description',
		...sharedProperties,
	},
	SPCatalogsBadgingLabelEnabled: {
		defaultValue: 'Enabled',
		description: `${columnDescriptionStart}Enabled.`,
		displayPath: `${pathStart}Enabled`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.ColumnHeading.Enabled',
		...sharedProperties,
	},
	SPCatalogsBadgingLabelImage: {
		defaultValue: 'Image',
		description: `${columnDescriptionStart}Image.`,
		displayPath: `${pathStart}Image`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.CmBadges.ColumnHeading.Image',
		...sharedProperties,
	},
};

export type SPCatalogsBadgingLocType = LocMap<SPCatalogsBadgingLocNames>;

export const LOC_KEYS = MapLocKeys<SPCatalogsBadgingLocNames>(spCatalogsBadgingLocs);
